import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Container, Col, Row } from 'reactstrap';
import { H1, P } from '../../../AbstractElements';
import PayCard from '../Payer/paycard';
import HeaderClient from '../HeaderClient';
import i18n from '../../../i18n';
import TurnstileWidget from '../../../Components/TurnstileWidget';
import {
    MAX_TICKET_SEARCH_LENGTH,
    buildTicketSearchUrl,
    getTicketSearchParam,
    isValidTicketSearchValue,
    normalizeTicketSearchValue,
} from './ticketSearchUrl';

const EMPTY_TICKET_ERROR = "Veuillez entrer un num\u00e9ro d'avis";
const INVALID_TICKET_ERROR = "Num\u00e9ro d'avis invalide";
const NUMBERS_ONLY_ERROR = 'Veuillez entrer des num\u00e9ros uniquement';
const TURNSTILE_ERROR = 'Veuillez compléter la vérification de sécurité';

const SearchTicket = () => {
    const [messageID, setMessageID] = useState('');
    const [sendTicketID, setSendTicketID] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [sendTurnstileToken, setSendTurnstileToken] = useState('');
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [sending, setSending] = useState(false);

    const startTicketSearch = useCallback((rawTicketID) => {
        const normalizedTicketID = normalizeTicketSearchValue(rawTicketID);

        setMessageID(normalizedTicketID);

        if (normalizedTicketID === '') {
            setError(true);
            setErrorMsg(EMPTY_TICKET_ERROR);
            return false;
        }

        if (!isValidTicketSearchValue(normalizedTicketID)) {
            setError(true);
            setErrorMsg(INVALID_TICKET_ERROR);
            return false;
        }

        if (!turnstileToken) {
            setError(true);
            setErrorMsg(TURNSTILE_ERROR);
            return false;
        }

        setError(false);
        setShowModal(true);
        setSendTicketID(normalizedTicketID);
        setSendTurnstileToken(turnstileToken);
        setTurnstileToken('');
        setTurnstileResetKey((current) => current + 1);
        return true;
    }, [turnstileToken]);

    const sendToModal = () => {
        startTicketSearch(messageID);
    };

    const handleChange = (event) => {
        let value = normalizeTicketSearchValue(event.target.value);

        if (value.length > MAX_TICKET_SEARCH_LENGTH) {
            value = value.substr(0, MAX_TICKET_SEARCH_LENGTH);
        }

        setMessageID(value);

        if (error) {
            setError(false);
        }
    };

    const handleTurnstileToken = useCallback((token) => {
        setTurnstileToken(String(token || ''));
    }, []);

    const sendingStatus = (status) => {
        setSending(status);
    };

    const updateUrlForTicket = (ticketNumber) => {
        const normalizedTicketID = normalizeTicketSearchValue(ticketNumber);

        if (!isValidTicketSearchValue(normalizedTicketID)) {
            return;
        }

        const nextUrl = buildTicketSearchUrl(normalizedTicketID);
        const currentUrl = `${window.location.pathname}${window.location.search}`;

        if (currentUrl !== nextUrl) {
            window.history.pushState({ avis: normalizedTicketID }, '', nextUrl);
        }
    };

    useEffect(() => {
        const ticketNumberFromUrl = getTicketSearchParam(window.location.search);

        if (ticketNumberFromUrl !== '') {
            setMessageID(ticketNumberFromUrl);
        }
    }, []);

    return (
        <Fragment>
            <HeaderClient />

            <Container fluid className="searchbox-container">
                <Row className="search-banner">
                    <Col md="12" lg="4" className="fit-content searchbox-bg">
                        <div className="searchTicket-search">
                            <div>
                                <H1 className="text-light">
                                    {i18n.t('SearchPage_Intro_Header')}
                                    <span className="f-color-primary f-w-700">
                                        {' '}
                                        {i18n.t('SearchPage_Intro_HeaderAccent')}
                                        {' '}
                                    </span>
                                    <br />
                                    {i18n.t('SearchPage_Intro_HeaderEnd')}
                                </H1>

                                <P className="text-light f-18">
                                    {i18n.t('SearchPage_Intro_Txt')}
                                    <br />
                                    {i18n.t('SearchPage_Intro_Txt2')}
                                </P>
                            </div>
                        </div>

                        <div className="pb-0 mb-12 col-12 p-r-0">
                            <div className="row">
                                <div className="xl-50 mb-6 col-5 p-r-0 mb-3">
                                    <input
                                        placeholder={i18n.t('SearchPage_Field_Searching_PlaceHolder')}
                                        value={messageID}
                                        onChange={handleChange}
                                        name="ID"
                                        type="text"
                                        className={`form-control search-bar ${!error ? '' : 'search-banner-error shake animated'}`}
                                        onKeyPress={(event) => {
                                            if (!/[0-9]/.test(event.key)) {
                                                event.preventDefault();
                                                setErrorMsg(NUMBERS_ONLY_ERROR);
                                                setError(true);
                                            }
                                        }}
                                    />
                                </div>

                                <div className="col-12 mb-3">
                                    <TurnstileWidget
                                        action="ticket_lookup"
                                        onToken={handleTurnstileToken}
                                        resetKey={turnstileResetKey}
                                    />
                                </div>

                                <div className="xl-50 mb-6 col-5 p-r-0 mb-3">
                                    <button
                                        className="btn btn-primary btn-maxpark btn-lg min-w-200 p-t-20 p-b-20"
                                        onClick={sendToModal}
                                        disabled={sending || !turnstileToken}
                                    >
                                        {!sending
                                            ? i18n.t('SearchPage_SearchBtn')
                                            : i18n.t('SearchPage_SearchBtn_Searching')}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <span className={`error-form ${!error ? 'hidden' : 'fadeIn'}`}>
                            {errorMsg}
                        </span>
                    </Col>

                    <Col md="12" lg="5">
                        <PayCard
                            showModalCard={showModal}
                            searchTicketID={sendTicketID}
                            turnstileToken={sendTurnstileToken}
                            paypage={false}
                            searchpage={true}
                            searching={sendingStatus}
                            onTicketFound={updateUrlForTicket}
                        />
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default SearchTicket;
